import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const FIELDS = [
  ["business_name", "Business / brand name", "text"],
  ["legal_entity", "Legal entity name", "text"],
  ["owner_name", "Beneficial owner name", "text"],
  ["tax_id", "GSTIN / Tax ID", "text"],
  ["bank_account", "Bank account number", "text"],
  ["phone", "Business phone", "tel"],
  ["address", "Warehouse / pickup address", "text"],
];

export default function SellerRegisterPage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ business_name: "", legal_entity: "", owner_name: "", tax_id: "", bank_account: "", phone: "", address: "", categories: [] });
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.seller) nav("/seller");
  }, [user, nav]);

  if (!user) {
    nav("/vendor/login", { state: { from: "/sell" } });
    return null;
  }

  const toggleCat = (name) => {
    setForm((f) => ({ ...f, categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/seller/register", form);
      await refresh();
      toast.success("Application submitted — under review by marketplace ops");
      nav("/seller");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="seller-register-page" className="max-w-2xl mx-auto px-8 mt-12">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center"><Store className="w-5 h-5" /></span>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Sell on Hello Nanba</h1>
          <p className="text-sm text-slate-500">Register your business. Applications are reviewed by marketplace ops.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, label, type]) => (
            <input key={k} data-testid={`seller-reg-${k}`} type={type} required value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={label}
              className={`h-12 rounded-md border border-slate-200 px-4 text-sm outline-none focus:border-blue-500 ${k === "address" ? "sm:col-span-2" : ""}`} />
          ))}
        </div>
        {categories.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Product categories</p>
            <div className="flex gap-2 flex-wrap">
              {categories.map((c) => (
                <button type="button" key={c.id} data-testid={`seller-cat-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => toggleCat(c.name)}
                  className={`text-xs px-4 py-2 rounded-full border transition-colors ${form.categories.includes(c.name) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <Button data-testid="seller-reg-submit" type="submit" disabled={busy} className="rounded-full bg-slate-900 px-10 h-12 font-semibold">
          {busy ? "Submitting…" : "Submit application"}
        </Button>
      </form>
    </div>
  );
}
